import * as z from 'zod';

export const CreateProgramDefinitionRequest = z.object({
  programDefinition: z
    .object({
      _id: z
        .string()
        .describe('Program definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the program definition is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the program definition.\n\nIgnored when creating a program definition.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the program definition was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program definition was updated.')
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe('Program definition name.')
        .max(64)
        .optional()
        .nullable(),
      namespace: z
        .string()
        .describe(
          "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
        )
        .min(1)
        .max(20),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the program definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      externalId: z
        .string()
        .describe(
          'ID for the program definition defined by you. You can use `externalId` to filter queries.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
    })
    .describe('Program definition to create.'),
});
export const CreateProgramDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Program definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the program definition is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the program definition.\n\nIgnored when creating a program definition.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the program definition was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the program definition was updated.')
    .optional()
    .nullable(),
  displayName: z
    .string()
    .describe('Program definition name.')
    .max(64)
    .optional()
    .nullable(),
  namespace: z
    .string()
    .describe(
      "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
    )
    .min(1)
    .max(20)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the program definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  externalId: z
    .string()
    .describe(
      'ID for the program definition defined by you. You can use `externalId` to filter queries.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const UpdateProgramDefinitionRequest = z.object({
  _id: z
    .string()
    .describe('Program definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  programDefinition: z
    .object({
      _id: z
        .string()
        .describe('Program definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the program definition is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the program definition.\n\nIgnored when creating a program definition.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the program definition was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program definition was updated.')
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe('Program definition name.')
        .max(64)
        .optional()
        .nullable(),
      namespace: z
        .string()
        .describe(
          "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
        )
        .min(1)
        .max(20)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the program definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      externalId: z
        .string()
        .describe(
          'ID for the program definition defined by you. You can use `externalId` to filter queries.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Program definition to update.'),
});
export const UpdateProgramDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Program definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the program definition is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the program definition.\n\nIgnored when creating a program definition.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the program definition was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the program definition was updated.')
    .optional()
    .nullable(),
  displayName: z
    .string()
    .describe('Program definition name.')
    .max(64)
    .optional()
    .nullable(),
  namespace: z
    .string()
    .describe(
      "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
    )
    .min(1)
    .max(20)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the program definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  externalId: z
    .string()
    .describe(
      'ID for the program definition defined by you. You can use `externalId` to filter queries.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const DeleteProgramDefinitionRequest = z.object({
  programDefinitionId: z
    .string()
    .describe('ID of the program definition to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      cascadeType: z
        .enum([
          'UNKNOWN_CASCADE',
          'IMMEDIATELY',
          'NEXT_RENEWAL',
          'FUTURE_PROVISIONS',
        ])
        .optional(),
    })
    .optional(),
});
export const DeleteProgramDefinitionResponse = z.object({});
export const GetProgramDefinitionRequest = z.object({
  programDefinitionId: z
    .string()
    .describe('ID of the program definition to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetProgramDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Program definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the program definition is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the program definition.\n\nIgnored when creating a program definition.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the program definition was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the program definition was updated.')
    .optional()
    .nullable(),
  displayName: z
    .string()
    .describe('Program definition name.')
    .max(64)
    .optional()
    .nullable(),
  namespace: z
    .string()
    .describe(
      "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
    )
    .min(1)
    .max(20)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the program definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  externalId: z
    .string()
    .describe(
      'ID for the program definition defined by you. You can use `externalId` to filter queries.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const GetProgramDefinitionByExternalIdAndNamespaceRequest = z.object({
  namespace: z
    .string()
    .describe(
      "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
    )
    .min(1)
    .max(20),
  options: z.object({
    externalId: z
      .string()
      .describe('Program definition external ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const GetProgramDefinitionByExternalIdAndNamespaceResponse = z.object({
  programDefinition: z
    .object({
      _id: z
        .string()
        .describe('Program definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the program definition is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the program definition.\n\nIgnored when creating a program definition.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the program definition was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program definition was updated.')
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe('Program definition name.')
        .max(64)
        .optional()
        .nullable(),
      namespace: z
        .string()
        .describe(
          "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
        )
        .min(1)
        .max(20)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the program definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      externalId: z
        .string()
        .describe(
          'ID for the program definition defined by you. You can use `externalId` to filter queries.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Retrieved program definition.')
    .optional(),
});
export const QueryProgramDefinitionsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          namespace: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          externalId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum(['_id', 'namespace', 'externalId', '_createdDate'])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Filter, sort, and paging to apply to the query.'),
});
export const QueryProgramDefinitionsResponse = z.object({
  programDefinitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Program definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the program definition is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the program definition.\n\nIgnored when creating a program definition.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the program definition was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the program definition was updated.')
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe('Program definition name.')
          .max(64)
          .optional()
          .nullable(),
        namespace: z
          .string()
          .describe(
            "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
          )
          .min(1)
          .max(20)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the program definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        externalId: z
          .string()
          .describe(
            'ID for the program definition defined by you. You can use `externalId` to filter queries.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
